import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Loader2 } from 'lucide-react';
import { useTranslation } from '../i18n/LanguageContext';

const ARCHETYPE_ICONS = {
  'the-skeptic': '🔍',
  'the-early-adopter': '🚀',
  'the-traditionalist': '🏛️',
  'the-pragmatist': '⚖️',
  'the-empath': '💚',
  'the-budget-watcher': '💰',
  'the-activist': '✊',
  'the-professional': '💼',
  'the-digital-native': '📱',
  'the-concerned-parent': '👨‍👧',
};

const CATEGORY_COLORS = {
  cognitive: 'from-blue-50 to-indigo-50 border-blue-200',
  social: 'from-green-50 to-emerald-50 border-green-200',
  consumer: 'from-amber-50 to-yellow-50 border-amber-200',
  professional: 'from-purple-50 to-violet-50 border-purple-200',
  demographic: 'from-rose-50 to-pink-50 border-rose-200',
  general: 'from-stone-50 to-stone-100 border-stone-200',
};

export function ArchetypeGallery({ library }) {
  const { t } = useTranslation();

  const handleGenerate = async (archetypeId) => {
    const persona = await library.generateFromArchetype(archetypeId);
    if (persona) {
      library.openEditor(persona);
    }
  };

  const grouped = {};
  (library.archetypes || []).forEach(a => {
    const cat = a.category || 'general';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(a);
  });

  return (
    <div className="space-y-8">
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-stone-200">
        <h2 className="font-serif text-2xl mb-2">{t('personaLib.archetypesTitle')}</h2>
        <p className="text-stone-500">{t('personaLib.archetypesDesc')}</p>
      </div>

      {Object.entries(grouped).map(([category, archetypes]) => (
        <div key={category}>
          <h3 className="text-sm font-medium text-stone-500 uppercase tracking-wide mb-3 capitalize">{category}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {archetypes.map(archetype => (
              <motion.div
                key={archetype.id}
                whileHover={{ scale: 1.02 }}
                className={`p-5 rounded-xl border bg-gradient-to-br cursor-pointer transition-shadow hover:shadow-md ${CATEGORY_COLORS[category] || CATEGORY_COLORS.general}`}
                onClick={() => handleGenerate(archetype.id)}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="text-3xl">{ARCHETYPE_ICONS[archetype.id] || '👤'}</div>
                  <button className="p-1.5 text-amber-600 hover:bg-amber-100 rounded-lg transition-colors" disabled={library.isAiWorking}>
                    {library.isAiWorking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  </button>
                </div>
                <h4 className="font-medium text-stone-900 mb-1">{archetype.name}</h4>
                <p className="text-sm text-stone-600">{archetype.description}</p>
                {archetype.template?.personality_traits && (
                  <div className="flex flex-wrap gap-1 mt-3">
                    {archetype.template.personality_traits.slice(0, 3).map(trait => (
                      <span key={trait} className="px-2 py-0.5 bg-white/70 text-stone-600 text-xs rounded">{trait}</span>
                    ))}
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      ))}

      {(library.archetypes || []).length === 0 && (
        <div className="text-center py-12 text-stone-400">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
          <p>{t('personaLib.loadingArchetypes')}</p>
        </div>
      )}
    </div>
  );
}
